/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file state/store for homegrown apps widget
 * @module Epic.AppOrchard.State.InProgressApps
 */

import { buildSharedState } from "@epic/react-redux-booster";
import { ClientApplicationStatus } from "ao/data";
import store from "ao/store";
import { CustomerAppChangeRequestType, ICustomerAppLicense, IHomegrownApp } from "ao/types";
import { searchHasMatch } from "ao/utils/helpers";

/// TYPES ///

export interface IState {
	// Map between the org ID and the in apps for a customer org
	readonly appsForOrg: Map<number, IHomegrownApp[]> | undefined;
	// Whether app data has been loaded for a customer org
	readonly hasLoadedForOrg: Map<number, boolean> | undefined;
	readonly orgCurrentEpicVersions: Map<number, number | null> | undefined;
	readonly isLocalJKUWarningEnabledForFeb24: boolean;
}

/**
 * Search params to identify the apps to select from the store
 */
interface IAppsSelectorParams {
	orgId: number;
	searchTerm: string;
	currentUserId: number;
	createdByCurrentUserOnly: boolean;
	changeRequestsOnly: boolean;
	includeAllRelatedOrgApps: boolean;
}

/**
 * Param set used to make sure we update all apps in the store after an update is made to the server
 * @param orgId org ID to use
 * @returns
 */
function getEmptyFilterParamsForOrg(orgId: number) {
	const emptyFilterParams: IAppsSelectorParams = {
		orgId: orgId,
		searchTerm: "",
		currentUserId: 0,
		createdByCurrentUserOnly: false,
		changeRequestsOnly: false,
		includeAllRelatedOrgApps: true,
	};
	return emptyFilterParams;
}

/// INIT ///

/**
 * Returns an empty initial state
 */
export function getInitialState(): IState {
	return {
		hasLoadedForOrg: undefined,
		appsForOrg: undefined,
		orgCurrentEpicVersions: undefined,
		isLocalJKUWarningEnabledForFeb24: false,
	};
}

//#region REDUCERS

/**
 * Make JS Date objects from string dates loaded from the server
 * @param app app to transform
 * @returns app with JS Date objects
 */
function makeAppJSDates(app: IHomegrownApp): IHomegrownApp {
	return {
		...app,
		createdOn: app.createdOn && new Date(app.createdOn),
		license: makeLicenseJSDates(app.license),
	};
}

/**
 * Make JS Date objects from string dates loaded from the server
 * @param app app to transform
 * @returns app with JS Date objects
 */
function makeLicenseJSDates(lic: ICustomerAppLicense): ICustomerAppLicense {
	return {
		...lic,
		nonProdLicensedOn: lic.nonProdLicensedOn && new Date(lic.nonProdLicensedOn),
		prodLicensedOn: lic.prodLicensedOn && new Date(lic.prodLicensedOn),
		changeRequestedOn: lic.changeRequestedOn && new Date(lic.changeRequestedOn),
	};
}

/**
 * Update apps for a customer org and mark apps as loaded from server
 * @param state current state
 * @param params reducer params (org ID and apps to add)
 * @returns updated state
 */
export function updateAppsAfterLoad(
	state: IState,
	params: {
		orgId: number;
		apps: IHomegrownApp[];
		orgCurrentEpicVersion: number | null;
		isLocalJKUWarningEnabled: boolean;
	},
): IState {
	const { orgId, apps, orgCurrentEpicVersion, isLocalJKUWarningEnabled } = params;

	// update state to indicate we've loaded data for this org
	var loadedMap = state.hasLoadedForOrg
		? new Map<number, boolean>([...Array.from(state.hasLoadedForOrg), [orgId, true]])
		: new Map<number, boolean>([[orgId, true]]);

	const appsEntryForOrg = apps.map((a) => makeAppJSDates(a));

	// update data loaded for the org
	const appsMap = state.appsForOrg
		? new Map<number, IHomegrownApp[]>([...Array.from(state.appsForOrg), [orgId, appsEntryForOrg]])
		: new Map<number, IHomegrownApp[]>([[orgId, appsEntryForOrg]]);

	const versionsMap = state.orgCurrentEpicVersions
		? new Map<number, number | null>([
				...Array.from(state.orgCurrentEpicVersions),
				[orgId, orgCurrentEpicVersion],
		  ])
		: new Map<number, number | null>([[orgId, orgCurrentEpicVersion]]);

	return {
		hasLoadedForOrg: loadedMap,
		appsForOrg: appsMap,
		orgCurrentEpicVersions: versionsMap,
		isLocalJKUWarningEnabledForFeb24: isLocalJKUWarningEnabled,
	};
}

export function markAsInternal(state: IState, params: { appId: number; orgId: number }): IState {
	const { appId, orgId } = params;
	const currentApps = getAppsOfType(state, getEmptyFilterParamsForOrg(orgId));
	const updatedApps = currentApps.map((app) =>
		app.appId !== appId
			? app
			: {
					...app,
					status: ClientApplicationStatus.Internal,
			  },
	);

	return {
		hasLoadedForOrg: state.hasLoadedForOrg,
		orgCurrentEpicVersions: state.orgCurrentEpicVersions,
		appsForOrg: new Map<number, IHomegrownApp[]>([
			...Array.from(state.appsForOrg!),
			[orgId, updatedApps],
		]),
		isLocalJKUWarningEnabledForFeb24: state.isLocalJKUWarningEnabledForFeb24,
	};
}

/**
 * Update (potentially deleting) change request after saving updates to server.
 * @param state current state
 * @param params reducer params to find org's app to update and how
 * @returns updated status
 */
export function updateChangeRequest(
	state: IState,
	params: {
		appId: number;
		orgId: number;
		changeType: CustomerAppChangeRequestType | null;
		comment: string | null;
	},
): IState {
	const { appId, orgId, changeType, comment } = params;
	const currentApps = getAppsOfType(state, getEmptyFilterParamsForOrg(orgId));
	const isDelete = changeType === null;
	const updatedApps = currentApps.map((app) =>
		app.appId !== appId
			? app
			: {
					...app,
					license: {
						...app.license,
						changeRequestType: changeType,
						changeRequestComment: isDelete ? null : comment,
						// set these fields to current time/user so
						// we don't need to reload anything from the server after update
						changeRequestedOn: isDelete ? null : new Date(),
						changeRequestedByUserName: isDelete ? null : "You",
					},
			  },
	);

	return {
		hasLoadedForOrg: state.hasLoadedForOrg,
		orgCurrentEpicVersions: state.orgCurrentEpicVersions,
		appsForOrg: new Map<number, IHomegrownApp[]>([
			...Array.from(state.appsForOrg!),
			[orgId, updatedApps],
		]),
		isLocalJKUWarningEnabledForFeb24: state.isLocalJKUWarningEnabledForFeb24,
	};
}

//#endregion

//#region SELECTORS

/**
 * Check whether data has been loaded for a customer org
 * @param state current state
 * @param orgId: customer org id
 * @returns
 */
export function getHasLoaded(state: IState, orgId: number): boolean {
	return state.hasLoadedForOrg !== undefined && state.hasLoadedForOrg.get(orgId) === true;
}

export function getOrgCurrentEpicVersion(state: IState, orgId: number) {
	if (state.orgCurrentEpicVersions !== undefined) {
		return state.orgCurrentEpicVersions.get(orgId);
	}
}

function hasDataForOrg(state: IState, orgId: number): boolean {
	return state.appsForOrg !== undefined && state.appsForOrg.get(orgId) !== undefined;
}

/**
 * Helper to filter apps by search term
 * @param searchTerm search term to filter apps on
 * @param app app being considered for inclusion in results
 * @returns true if passes filter else false
 */
function filterBySearchTerm(searchTerm: string, app: IHomegrownApp): boolean {
	return (
		searchHasMatch(searchTerm, app.appName) ||
		searchHasMatch(searchTerm, app.prodClientId) ||
		searchHasMatch(searchTerm, app.nonProdClientId)
	);
}

/**
 * Shared helper that filters to apps of a given type + search term filtering
 *
 * Checks if data has been loaded for the org too
 * @param state current state
 * @param orgId customer org ID
 * @param searchTerm search term to further filter matching apps
 * @param filterFn function to filter to type of app
 * @returns list of apps matching filters
 */
function getAppsOfType(
	state: IState,
	filterParams: IAppsSelectorParams,
	filterFn?: ((app: IHomegrownApp) => boolean) | undefined,
): IHomegrownApp[] {
	const {
		orgId,
		searchTerm,
		currentUserId,
		createdByCurrentUserOnly,
		changeRequestsOnly,
		includeAllRelatedOrgApps,
	} = filterParams;
	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return [];
	}
	let apps = state.appsForOrg!.get(orgId)!;

	if (createdByCurrentUserOnly && apps.length > 0) {
		apps = apps.filter((a) => a.createdBy === currentUserId);
	}

	if (changeRequestsOnly && apps.length > 0) {
		apps = apps.filter((a) => a.license.changeRequestType !== null);
	}

	if (!includeAllRelatedOrgApps && apps.length > 0) {
		apps = apps.filter(
			(a) => !a.owningOrgName || a.license.isActivatedInNonProd || a.license.isActivatedInProd,
		);
	}

	if (filterFn && apps.length > 0) {
		apps = apps.filter(filterFn);
	}

	if (searchTerm && apps.length > 0) {
		apps = apps.filter((a) => filterBySearchTerm(searchTerm, a));
	}

	return apps;
}

/**
 * Get all draft apps
 * @param state current state
 * @param params filter params
 */
export function getDraftApps(state: IState, filterParams: IAppsSelectorParams): IHomegrownApp[] {
	const apps = getAppsOfType(
		state,
		filterParams,
		(a) => !a.license.isActivatedInNonProd && !a.license.isActivatedInProd,
	);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get apps that have been activated for testing
 * @param state current state
 * @param params filter params
 */
export function getAppsActivatedForTesting(
	state: IState,
	filterParams: IAppsSelectorParams,
): IHomegrownApp[] {
	const apps = getAppsOfType(
		state,
		filterParams,
		(a) => a.license.isActivatedInNonProd === true && !a.license.isActivatedInProd,
	);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get apps that have been activated for production
 * @param state current state
 * @param params filter params
 */
export function getAppsActivatedForProd(state: IState, filterParams: IAppsSelectorParams): IHomegrownApp[] {
	const apps = getAppsOfType(state, filterParams, (a) => a.license.isActivatedInProd === true);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

export function getIsLocalJKUWarningEnabledForFeb24(state: IState): boolean {
	return state.isLocalJKUWarningEnabledForFeb24;
}

//#endregion

/// BUILD IT ///

const builtState = buildSharedState({
	init: getInitialState,
	reducers: {
		updateAppsAfterLoad,
		markAsInternal,
		updateChangeRequest,
	},
	selectors: {
		getHasLoaded,
		getDraftApps,
		getAppsActivatedForTesting,
		getAppsActivatedForProd,
		getOrgCurrentEpicVersion,
		getIsLocalJKUWarningEnabledForFeb24,
	},
});

store.addSharedState(builtState.sharedState, "homegrownApps");

export const { actionCreators: homegrownAppsActions, useSharedState: useHomegrownAppsState } = builtState;
